import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import BreadcrumbTrail from '../../components/navigation/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import characterService from '../../services/characterService';
import { supabase } from '../../lib/supabase';
import AICharacterAnalysis from './components/AICharacterAnalysis';



const CharacterManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile, isAuthenticated } = useAuth();
  const [playId, setPlayId] = useState(null);
  const [playData, setPlayData] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [filteredCharacters, setFilteredCharacters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [completionFilter, setCompletionFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    roleClassification: 'supporting',
    description: ''
  });
  const [validationReport, setValidationReport] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [showAIAnalysis, setShowAIAnalysis] = useState(false);

  const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'superadmin';

  useEffect(() => {
    if (location?.state?.playId) {
      setPlayId(location?.state?.playId);
    }
  }, [location]);

  useEffect(() => {
    if (playId && isAuthenticated) {
      fetchPlayData();
      fetchCharacters();
    }
  }, [playId, isAuthenticated]);

  useEffect(() => {
    applyFilters();
  }, [characters, searchQuery, roleFilter, completionFilter]);

  const fetchPlayData = async () => {
    try {
      const { data, error } = await supabase
        ?.from('plays')
        ?.select('*')
        ?.eq('id', playId)
        ?.single();

      if (error) throw error;
      setPlayData(data);
    } catch (err) {
      console.error('Error fetching play data:', err);
      setError('Failed to load play details');
    }
  };

  const fetchCharacters = async () => {
    try {
      setIsLoading(true);
      const data = await characterService?.getCharactersByPlayId(playId);
      setCharacters(data || []);
      setFilteredCharacters(data || []);
    } catch (err) {
      console.error('Error fetching characters:', err);
      setError('Failed to load character data');
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = characters;

    // Search filter
    if (searchQuery) {
      filtered = filtered?.filter(char =>
        char?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered?.filter(char => char?.roleClassification === roleFilter);
    }

    setFilteredCharacters(filtered);
  };

  const handleAddCharacter = async () => {
    try {
      setError(null);
      await characterService?.createCharacter(playId, formData);
      setShowAddModal(false);
      setFormData({ name: '', roleClassification: 'supporting', description: '' });
      await fetchCharacters();
    } catch (err) {
      console.error('Error adding character:', err);
      setError(err?.message || 'Failed to add character');
    }
  };

  const handleEditCharacter = async () => {
    try {
      setError(null);
      await characterService?.updateCharacter(selectedCharacter?.id, formData);
      setShowEditModal(false);
      setSelectedCharacter(null);
      setFormData({ name: '', roleClassification: 'supporting', description: '' });
      await fetchCharacters();
    } catch (err) {
      console.error('Error updating character:', err);
      setError(err?.message || 'Failed to update character');
    }
  };

  const handleDeleteCharacter = async (characterId) => {
    if (!window.confirm('Are you sure you want to delete this character? This action cannot be undone.')) {
      return;
    }

    try {
      setError(null);
      await characterService?.deleteCharacter(characterId);
      await fetchCharacters();
    } catch (err) {
      console.error('Error deleting character:', err);
      setError(err?.message || 'Failed to delete character');
    }
  };

  const handleApplyAISuggestion = async (characterName, suggestedRole) => {
    const character = characters?.find(
      c => c?.name?.toUpperCase()?.trim() === characterName?.toUpperCase()?.trim()
    );
    if (!character) throw new Error(`Character "${characterName}" not found`);
    await characterService?.updateCharacter(character?.id, {
      ...character,
      roleClassification: suggestedRole
    });
    await fetchCharacters();
  };

  const openEditModal = (character) => {
    setSelectedCharacter(character);
    setFormData({
      name: character?.name,
      roleClassification: character?.roleClassification,
      description: character?.description || ''
    });
    setShowEditModal(true);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'protagonist':
        return 'bg-primary/10 text-primary';
      case 'antagonist':
        return 'bg-destructive/10 text-destructive';
      case 'supporting':
        return 'bg-success/10 text-success';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const runValidation = async () => {
    if (!playId) return;
    setIsValidating(true);
    setShowValidation(true);
    try {
      // Fetch all lines for this play from the database
      const { data: linesData, error: linesError } = await supabase
        ?.from('lines')
        ?.select('line_id, line_number, character, dialogue, is_stage_direction, scene_id')
        ?.eq('play_id', playId)
        ?.order('line_number', { ascending: true });

      if (linesError) throw linesError;

      const lines = (linesData || [])?.map(row => ({
        lineId: row?.line_id,
        lineNumber: row?.line_number,
        character: row?.character,
        dialogue: row?.dialogue,
        lineType: row?.is_stage_direction || 'LINE'
      }));

      // Build character name list from the characters table
      const characterNames = characters?.map(c => c?.name?.toUpperCase()?.trim());

      // Reconstruct parsedData shape expected by validateCharacterExtraction
      const parsedData = {
        rawData: linesData || [],
        lines,
        characters: characterNames
      };

      // Run validation inline (import the function)
      const characterTypeRows = lines?.filter(l => l?.lineType === 'CHARACTER');
      const hasCharacterRows = characterTypeRows?.length > 0;
      const issues = [];
      const warnings = [];
      const characterDetails = [];

      if (!hasCharacterRows) {
        issues?.push({
          type: 'MISSING_CHARACTER_ROWS',
          severity: 'error',
          message: 'No CHARACTER line type rows found in the database for this play.',
          detail: 'Ensure the is_stage_direction column contains "CHARACTER" for rows that define character names.'
        });
      } else {
        const emptyCharacterRows = characterTypeRows?.filter(l => !l?.dialogue?.trim());
        if (emptyCharacterRows?.length > 0) {
          issues?.push({
            type: 'EMPTY_CHARACTER_ROWS',
            severity: 'error',
            message: `${emptyCharacterRows?.length} CHARACTER row(s) have empty text (character name missing).`,
            detail: `Line IDs: ${emptyCharacterRows?.slice(0, 5)?.map(l => l?.lineId)?.join(', ')}${emptyCharacterRows?.length > 5 ? '...' : ''}`
          });
        }
      }

      const lineCountMap = new Map();
      const speakerSet = new Set();
      lines?.forEach(line => {
        if (line?.lineType === 'LINE' && line?.character) {
          const key = line?.character?.toUpperCase()?.trim();
          speakerSet?.add(key);
          lineCountMap?.set(key, (lineCountMap?.get(key) || 0) + 1);
        }
      });

      const extractedCharacters = new Set(
        characterTypeRows.map(l => l?.dialogue?.toUpperCase()?.trim()).filter(Boolean)
      );

      extractedCharacters?.forEach(charName => {
        const lineCount = lineCountMap?.get(charName) || 0;
        const dbChar = characters?.find(c => c?.name?.toUpperCase()?.trim() === charName);
        const dbLineCount = dbChar?.lineCount ?? null;
        const lineCountMatch = dbLineCount !== null ? dbLineCount === lineCount : null;

        characterDetails?.push({
          name: charName,
          source: 'CHARACTER_ROW',
          computedLineCount: lineCount,
          dbLineCount,
          lineCountMatch,
          inCharacterManagement: !!dbChar,
          hasSpeakingLines: lineCount > 0
        });

        if (lineCount === 0) {
          warnings?.push({
            type: 'CHARACTER_NO_LINES',
            severity: 'warning',
            message: `"${charName}" is defined in a CHARACTER row but has 0 speaking lines.`,
            detail: 'This may be intentional or a speaker column mismatch.'
          });
        }
        if (!dbChar) {
          issues?.push({
            type: 'CHARACTER_NOT_IN_MANAGEMENT',
            severity: 'error',
            message: `"${charName}" was found in CHARACTER rows but is missing from Character Management.`,
            detail: 'This character was not saved to the characters table during CSV import.'
          });
        } else if (lineCountMatch === false) {
          warnings?.push({
            type: 'LINE_COUNT_MISMATCH',
            severity: 'warning',
            message: `"${charName}" line count mismatch: database shows ${dbLineCount}, computed from lines is ${lineCount}.`,
            detail: 'The stored line_count may be stale. Re-importing the CSV will recalculate it.'
          });
        }
      });

      speakerSet?.forEach(speaker => {
        if (!extractedCharacters?.has(speaker)) {
          const lineCount = lineCountMap?.get(speaker) || 0;
          const dbChar = characters?.find(c => c?.name?.toUpperCase()?.trim() === speaker);
          characterDetails?.push({
            name: speaker,
            source: 'LINE_SPEAKER',
            computedLineCount: lineCount,
            dbLineCount: dbChar?.lineCount ?? null,
            lineCountMatch: dbChar ? dbChar?.lineCount === lineCount : null,
            inCharacterManagement: !!dbChar,
            hasSpeakingLines: true
          });
          warnings?.push({
            type: 'SPEAKER_WITHOUT_CHARACTER_ROW',
            severity: 'warning',
            message: `Speaker "${speaker}" appears in LINE rows but has no CHARACTER row.`,
            detail: `Has ${lineCount} line(s). Add a CHARACTER row to properly define them.`
          });
        }
      });

      const unassignedLines = lines?.filter(l => l?.lineType === 'LINE' && !l?.character)?.length;
      if (unassignedLines > 0) {
        warnings?.push({
          type: 'UNASSIGNED_LINES',
          severity: 'warning',
          message: `${unassignedLines} LINE row(s) have no speaker assigned.`,
          detail: 'These lines are not counted toward any character.'
        });
      }

      const totalLineRows = lines?.filter(l => l?.lineType === 'LINE')?.length;
      const totalSpeakingLines = Array.from(lineCountMap?.values())?.reduce((s, c) => s + c, 0);

      setValidationReport({
        isValid: issues?.length === 0,
        summary: {
          totalRawRows: linesData?.length || 0,
          totalLineRows,
          totalCharacterRows: characterTypeRows?.length,
          totalStageDirectionRows: lines?.filter(l => l?.lineType === 'STAGE_DIRECTION')?.length,
          extractedCharacterCount: extractedCharacters?.size,
          speakingCharacterCount: speakerSet?.size,
          managementCharacterCount: characters?.length,
          totalSpeakingLines,
          unassignedLines
        },
        issues,
        warnings,
        characterDetails: characterDetails?.sort((a, b) => b?.computedLineCount - a?.computedLineCount)
      });
    } catch (err) {
      console.error('Validation error:', err);
      setValidationReport({ error: err?.message || 'Validation failed' });
    } finally {
      setIsValidating(false);
    }
  };

  if (!playId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">No Play Selected</h2>
          <p className="text-muted-foreground mb-4">Please select a play from the Edit Play page</p>
          <Button onClick={() => navigate('/play-management')}>
            Go to Play Management
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation
        user={userProfile}
        isAuthenticated={isAuthenticated}
        notifications={[]}
      />
      <main className="main-content">
        <BreadcrumbTrail />

        <div className="max-w-screen-2xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/edit-play', { state: { playId } })}
                iconName="ArrowLeft"
              >
                Back to Edit Play
              </Button>
            </div>

            {playData && (
              <div className="bg-card rounded-lg border border-border p-6 mb-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h1 className="text-3xl font-bold text-card-foreground mb-2">
                      Character Management
                    </h1>
                    <p className="text-lg text-muted-foreground">
                      {playData?.title} by {playData?.author}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAIAnalysis(prev => !prev)}
                      iconName="Sparkles"
                    >
                      {showAIAnalysis ? 'Hide AI Analysis' : 'AI Analysis'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={runValidation}
                      disabled={isValidating}
                      iconName={isValidating ? 'Loader2' : 'ShieldCheck'}
                    >
                      {isValidating ? 'Validating...' : 'Validate Characters'}
                    </Button>
                    {isAdmin && (
                      <Button onClick={() => setShowAddModal(true)} iconName="Plus">
                        Add Character
                      </Button>
                    )}
                  </div>
                </div>

                {/* Stats Overview */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-muted rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Users2" size={18} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Total Characters</span>
                    </div>
                    <p className="text-2xl font-bold text-card-foreground">{characters?.length || 0}</p>
                  </div>

                  <div className="bg-muted rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Crown" size={18} className="text-primary" />
                      <span className="text-sm text-muted-foreground">Protagonists</span>
                    </div>
                    <p className="text-2xl font-bold text-card-foreground">
                      {characters?.filter(c => c?.roleClassification === 'protagonist')?.length || 0}
                    </p>
                  </div>

                  <div className="bg-muted rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Swords" size={18} className="text-destructive" />
                      <span className="text-sm text-muted-foreground">Antagonists</span>
                    </div>
                    <p className="text-2xl font-bold text-card-foreground">
                      {characters?.filter(c => c?.roleClassification === 'antagonist')?.length || 0}
                    </p>
                  </div>

                  <div className="bg-muted rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Users" size={18} className="text-success" />
                      <span className="text-sm text-muted-foreground">Supporting</span>
                    </div>
                    <p className="text-2xl font-bold text-card-foreground">
                      {characters?.filter(c => c?.roleClassification === 'supporting')?.length || 0}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-center gap-3 mb-6">
                <Icon name="AlertCircle" size={20} className="text-destructive" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {/* AI Character Analysis Panel */}
            {showAIAnalysis && (
              <AICharacterAnalysis
                characters={characters}
                playData={playData}
                onApplySuggestion={isAdmin ? handleApplyAISuggestion : null}
              />
            )}

            {/* Validation Report Panel */}
            {showValidation && validationReport && !validationReport?.error && (
              <div className="bg-card rounded-lg border border-border p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Icon
                      name={validationReport?.isValid ? 'ShieldCheck' : 'ShieldAlert'}
                      size={22}
                      className={validationReport?.isValid ? 'text-success' : 'text-destructive'}
                    />
                    <h2 className="text-xl font-semibold text-card-foreground">Character Extraction Validation</h2>
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                      validationReport?.isValid
                        ? 'bg-success/10 text-success' :'bg-destructive/10 text-destructive'
                    }`}>
                      {validationReport?.isValid ? 'PASSED' : 'ISSUES FOUND'}
                    </span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setShowValidation(false)} iconName="X" />
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-5">
                  {[
                    { label: 'Total Rows', value: validationReport?.summary?.totalRawRows, icon: 'FileText' },
                    { label: 'LINE Rows', value: validationReport?.summary?.totalLineRows, icon: 'MessageSquare' },
                    { label: 'CHARACTER Rows', value: validationReport?.summary?.totalCharacterRows, icon: 'UserCheck' },
                    { label: 'Stage Directions', value: validationReport?.summary?.totalStageDirectionRows, icon: 'Clapperboard' },
                    { label: 'Extracted Chars', value: validationReport?.summary?.extractedCharacterCount, icon: 'Users2' },
                    { label: 'Speaking Chars', value: validationReport?.summary?.speakingCharacterCount, icon: 'Mic' },
                    { label: 'In Management', value: validationReport?.summary?.managementCharacterCount, icon: 'Database' },
                    { label: 'Unassigned Lines', value: validationReport?.summary?.unassignedLines, icon: 'AlertTriangle', highlight: validationReport?.summary?.unassignedLines > 0 }
                  ]?.map((stat, i) => (
                    <div key={i} className={`rounded-lg p-3 text-center ${
                      stat?.highlight ? 'bg-warning/10 border border-warning/20' : 'bg-muted'
                    }`}>
                      <Icon name={stat?.icon} size={14} className="text-muted-foreground mx-auto mb-1" />
                      <p className={`text-lg font-bold ${
                        stat?.highlight && stat?.value > 0 ? 'text-warning' : 'text-card-foreground'
                      }`}>{stat?.value ?? 0}</p>
                      <p className="text-xs text-muted-foreground leading-tight">{stat?.label}</p>
                    </div>
                  ))}
                </div>

                {/* Issues */}
                {validationReport?.issues?.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-destructive mb-2 flex items-center gap-2">
                      <Icon name="XCircle" size={16} />
                      Errors ({validationReport?.issues?.length})
                    </h3>
                    <div className="space-y-2">
                      {validationReport?.issues?.map((issue, i) => (
                        <div key={i} className="bg-destructive/5 border border-destructive/20 rounded-lg p-3">
                          <p className="text-sm font-medium text-destructive">{issue?.message}</p>
                          {issue?.detail && (
                            <p className="text-xs text-muted-foreground mt-1">{issue?.detail}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Warnings */}
                {validationReport?.warnings?.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-warning mb-2 flex items-center gap-2">
                      <Icon name="AlertTriangle" size={16} />
                      Warnings ({validationReport?.warnings?.length})
                    </h3>
                    <div className="space-y-2">
                      {validationReport?.warnings?.map((warn, i) => (
                        <div key={i} className="bg-warning/5 border border-warning/20 rounded-lg p-3">
                          <p className="text-sm font-medium text-warning">{warn?.message}</p>
                          {warn?.detail && (
                            <p className="text-xs text-muted-foreground mt-1">{warn?.detail}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Per-Character Details Table */}
                {validationReport?.characterDetails?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-card-foreground mb-3 flex items-center gap-2">
                      <Icon name="Table" size={16} />
                      Character Extraction Details
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-2 px-3 text-muted-foreground font-medium">Character</th>
                            <th className="text-left py-2 px-3 text-muted-foreground font-medium">Source</th>
                            <th className="text-right py-2 px-3 text-muted-foreground font-medium">Lines (Computed)</th>
                            <th className="text-right py-2 px-3 text-muted-foreground font-medium">Lines (DB)</th>
                            <th className="text-center py-2 px-3 text-muted-foreground font-medium">Count Match</th>
                            <th className="text-center py-2 px-3 text-muted-foreground font-medium">In Management</th>
                          </tr>
                        </thead>
                        <tbody>
                          {validationReport?.characterDetails?.map((char, i) => (
                            <tr key={i} className="border-b border-border/50 hover:bg-muted/30">
                              <td className="py-2 px-3 font-medium text-card-foreground">{char?.name}</td>
                              <td className="py-2 px-3">
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  char?.source === 'CHARACTER_ROW' ?'bg-primary/10 text-primary' :'bg-warning/10 text-warning'
                                }`}>
                                  {char?.source === 'CHARACTER_ROW' ? 'CHARACTER row' : 'LINE speaker'}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-right text-card-foreground">{char?.computedLineCount}</td>
                              <td className="py-2 px-3 text-right text-muted-foreground">
                                {char?.dbLineCount !== null ? char?.dbLineCount : '—'}
                              </td>
                              <td className="py-2 px-3 text-center">
                                {char?.lineCountMatch === null ? (
                                  <span className="text-muted-foreground text-xs">N/A</span>
                                ) : char?.lineCountMatch ? (
                                  <Icon name="CheckCircle" size={16} className="text-success mx-auto" />
                                ) : (
                                  <Icon name="XCircle" size={16} className="text-destructive mx-auto" />
                                )}
                              </td>
                              <td className="py-2 px-3 text-center">
                                {char?.inCharacterManagement ? (
                                  <Icon name="CheckCircle" size={16} className="text-success mx-auto" />
                                ) : (
                                  <Icon name="XCircle" size={16} className="text-destructive mx-auto" />
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {validationReport?.isValid && validationReport?.warnings?.length === 0 && (
                  <div className="flex items-center gap-3 bg-success/10 border border-success/20 rounded-lg p-4">
                    <Icon name="CheckCircle" size={20} className="text-success" />
                    <p className="text-sm text-success font-medium">
                      All characters are correctly extracted from CHARACTER rows and line counts are accurate.
                    </p>
                  </div>
                )}
              </div>
            )}

            {showValidation && validationReport?.error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-center gap-3 mb-6">
                <Icon name="AlertCircle" size={20} className="text-destructive" />
                <p className="text-sm text-destructive">Validation failed: {validationReport?.error}</p>
              </div>
            )}

            {/* Filters */}
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-card-foreground">Character Database</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Input
                  type="search"
                  placeholder="Search characters..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="md:col-span-2"
                />

                <Select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e?.target?.value)}
                >
                  <option value="all">All Roles</option>
                  <option value="protagonist">Protagonist</option>
                  <option value="antagonist">Antagonist</option>
                  <option value="supporting">Supporting</option>
                  <option value="minor">Minor</option>
                </Select>
              </div>

              {/* Character Grid */}
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <>
                  {filteredCharacters?.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {filteredCharacters?.map((character) => (
                        <div
                          key={character?.id}
                          className="bg-muted rounded-lg border border-border p-4"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h3 className="text-lg font-bold text-card-foreground mb-1">
                                {character?.name}
                              </h3>
                              <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getRoleColor(character?.roleClassification)}`}>
                                {character?.roleClassification}
                              </span>
                            </div>
                            {isAdmin && (
                              <div className="flex gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditModal(character)}
                                  iconName="Edit"
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteCharacter(character?.id)}
                                  iconName="Trash2"
                                  className="text-destructive hover:text-destructive"
                                />
                              </div>
                            )}
                          </div>

                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div className="flex items-center gap-2">
                              <Icon name="MessageSquare" size={16} className="text-muted-foreground" />
                              <span className="text-sm text-card-foreground">
                                {character?.lineCount} lines
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Icon name="Film" size={16} className="text-muted-foreground" />
                              <span className="text-sm text-card-foreground">
                                {character?.sceneAppearances || 0} scenes
                              </span>
                            </div>
                          </div>

                          {character?.description && (
                            <p className="text-sm text-muted-foreground mt-2">
                              {character?.description}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-card-foreground mb-2">
                        No characters found
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Try adjusting your search or filter criteria
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>
      {/* Add Character Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg border border-border max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-card-foreground">Add New Character</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowAddModal(false);
                  setFormData({ name: '', roleClassification: 'supporting', description: '' });
                }}
                iconName="X"
              />
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Character Name *
                </label>
                <Input
                  type="text"
                  value={formData?.name}
                  onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                  placeholder="Enter character name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Role Classification
                </label>
                <Select
                  value={formData?.roleClassification}
                  onChange={(e) => setFormData({ ...formData, roleClassification: e?.target?.value })}
                >
                  <option value="protagonist">Protagonist</option>
                  <option value="antagonist">Antagonist</option>
                  <option value="supporting">Supporting</option>
                  <option value="minor">Minor</option>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Description
                </label>
                <textarea
                  value={formData?.description}
                  onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                  placeholder="Enter character description (optional)"
                  rows={3}
                  className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddModal(false);
                    setFormData({ name: '', roleClassification: 'supporting', description: '' });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddCharacter}
                  disabled={!formData?.name}
                  className="flex-1"
                >
                  Add Character
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Edit Character Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg border border-border max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-card-foreground">Edit Character</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedCharacter(null);
                  setFormData({ name: '', roleClassification: 'supporting', description: '' });
                }}
                iconName="X"
              />
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Character Name *
                </label>
                <Input
                  type="text"
                  value={formData?.name}
                  onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                  placeholder="Enter character name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Role Classification
                </label>
                <Select
                  value={formData?.roleClassification}
                  onChange={(e) => setFormData({ ...formData, roleClassification: e?.target?.value })}
                >
                  <option value="protagonist">Protagonist</option>
                  <option value="antagonist">Antagonist</option>
                  <option value="supporting">Supporting</option>
                  <option value="minor">Minor</option>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Description
                </label>
                <textarea
                  value={formData?.description}
                  onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                  placeholder="Enter character description (optional)"
                  rows={3}
                  className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedCharacter(null);
                    setFormData({ name: '', roleClassification: 'supporting', description: '' });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleEditCharacter}
                  disabled={!formData?.name}
                  className="flex-1"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CharacterManagement;